import { api } from "./api";
import type {
  AppCreateRequest,
  AppCreateResponse,
  AppDeleteResponse,
  AppDetailOut,
  AppListResponse,
  AppPairResponse,
  AppPatchRequest,
  AppPatchResponse,
  AppPlatform,
  AppSelectRequest,
  AppSelectResponse,
  AppSiblingResponse,
  BaselineRefreshResponse,
  ConsoleDiscoveryResponse,
  ConsoleExperimentListResponse,
  DiscoveredLocalesResponse,
  ExperimentImportRequest,
  ExperimentImportResponse,
  LiveConnectSessionOut,
  ManualRefreshJobClass,
  ManualRefreshResponse,
  MonthlyOverviewListResponse,
  OnboardingBriefWriteRequest,
  OnboardingBriefWriteResponse,
  StoreSearchResponse,
} from "@/types";

// Source: BRIEF_ia-redesign-backend-endpoints § 8 + § 3.1, 3.2
// + R1b detail/patch (docs/product/technical-architecture.md § App Entity).

export async function listApps(
  workspaceId: string,
  userId: string,
): Promise<AppListResponse> {
  const { data } = await api.get<AppListResponse>("/api/v1/apps", {
    params: { workspace_id: workspaceId, user_id: userId },
  });
  return data;
}

export async function selectApp(
  appId: string,
  payload: AppSelectRequest,
  idempotencyKey: string,
): Promise<AppSelectResponse> {
  const { data } = await api.post<AppSelectResponse>(
    `/api/v1/apps/${encodeURIComponent(appId)}/select`,
    payload,
    { headers: { "Idempotency-Key": idempotencyKey } },
  );
  return data;
}

/**
 * Queue an operator-triggered baseline re-fetch (the Re-baseline button on
 * Creatives — ENG-CONTENT-SCRAPE-TRIGGER-REDESIGN C2).
 *
 * `POST /api/v1/apps/{app_id}/baseline:refresh` emits a
 * `baseline_refresh_requested` outbox event so the worker re-runs the CHEAP
 * public-HTTP baseline capture (screenshots / feature graphic / copy). The
 * automatic triggers (winner-apply + weekly periodic) already keep baselines
 * fresh; this is the on-demand escape hatch. Idempotency-Key REQUIRED (same-key
 * replays the recorded 202); the request body is empty.
 */
export async function triggerBaselineRefresh(
  appId: string,
  idempotencyKey: string,
): Promise<BaselineRefreshResponse> {
  const { data } = await api.post<BaselineRefreshResponse>(
    `/api/v1/apps/${encodeURIComponent(appId)}/baseline:refresh`,
    {},
    { headers: { "Idempotency-Key": idempotencyKey } },
  );
  return data;
}

/**
 * Read the full App entity (R1b column set).
 *
 * Settings → Confidence & Speed slider, Mode badge, Quality-Gate UX
 * banner, and the Stage 3.5 whole-app context aggregator consume this.
 */
export async function getApp(appId: string): Promise<AppDetailOut> {
  const { data } = await api.get<AppDetailOut>(
    `/api/v1/apps/${encodeURIComponent(appId)}`,
  );
  return data;
}

/**
 * GET /api/v1/apps/{app_id}/sibling — fetch the Android/iOS sibling app
 * (same product, opposite platform) for cross-platform program sharing
 * (HTTP_API.md `GET /api/v1/apps/{app_id}/sibling`).
 *
 * Source: ENG-FE-V2-CROSS-APP-PROGRAM-SHARING.
 *
 * Returns 200 with all-null fields when no sibling exists (single-
 * platform product is the normal case). The caller renders the
 * "Share with sibling" toggle only when `sibling_app_id !== null`.
 */
export async function getAppSibling(
  appId: string,
): Promise<AppSiblingResponse> {
  const { data } = await api.get<AppSiblingResponse>(
    `/api/v1/apps/${encodeURIComponent(appId)}/sibling`,
  );
  return data;
}

/**
 * GET /api/v1/apps/{app_id}/console-experiments — the app's RUNNING store
 * tests that ASOLoop does not track (HTTP_API.md; ENG-STORE-EXPERIMENT-
 * INVENTORY-SYNC). Read-only observations the daily inventory lane takes
 * from the store's own listing; `items: []` when none run or the lane has
 * not listed the app yet. A foreign app 404s.
 */
export async function listConsoleExperiments(
  appId: string,
): Promise<ConsoleExperimentListResponse> {
  const { data } = await api.get<ConsoleExperimentListResponse>(
    `/api/v1/apps/${encodeURIComponent(appId)}/console-experiments`,
  );
  return data;
}

/**
 * GET /api/v1/apps/{app_id}/discovered-locales — the app's auto-discovered
 * target-locale universe for the experiment / program locale dropdown
 * (ENG-LOCALE-AUTO-DISCOVERY, HTTP_API.md). Union of the app's localized
 * locales + locale-expansion traffic candidates (locales whose store traffic
 * cleared the per-platform Trigger-1 floor). Resolves by app_id; returns an
 * empty list when the app has no discovered locales yet (store analytics not
 * connected) so the caller falls back to the operator's current selection.
 */
export async function getDiscoveredLocales(
  appId: string,
): Promise<DiscoveredLocalesResponse> {
  const { data } = await api.get<DiscoveredLocalesResponse>(
    `/api/v1/apps/${encodeURIComponent(appId)}/discovered-locales`,
  );
  return data;
}

/**
 * GET /api/v1/apps/discover-console — list the apps the workspace's connected
 * store-console session exposes, for the Onboarding Screen-1 multi-app picker
 * (ENG-ONBOARDING-LIVE-CONNECT sub-3, HTTP_API.md).
 *
 * Dormant until the fixture-gated Playwright list-apps scrape adapter
 * registers: the response carries `session_connected` + `discovery_pending=true`
 * + `apps=[]`. The panel reads `discovery_pending` to keep the operator on the
 * connect step (or the manual fallback) rather than treating an empty `apps`
 * as "this account has zero apps".
 */
export async function discoverConsoleApps(
  workspaceId: string,
  platform: AppPlatform,
): Promise<ConsoleDiscoveryResponse> {
  const { data } = await api.get<ConsoleDiscoveryResponse>(
    "/api/v1/apps/discover-console",
    { params: { workspace_id: workspaceId, platform } },
  );
  return data;
}

/**
 * POST /api/v1/console-connect/sessions — launch a self-hosted live-login
 * session (ENG-ONBOARDING-LIVE-CONNECT sub-1/2). The returned `state` drives
 * the onboarding panel: `awaiting_login` mounts the live viewport;
 * `runner_unavailable` (the dormant default until the Fly runner ships) /
 * `failed` fall back to the manual secure connect.
 */
export async function launchConsoleConnect(
  workspaceId: string,
  platform: AppPlatform,
): Promise<LiveConnectSessionOut> {
  const { data } = await api.post<LiveConnectSessionOut>(
    "/api/v1/console-connect/sessions",
    { workspace_id: workspaceId, platform },
  );
  return data;
}

/**
 * GET /api/v1/console-connect/sessions/{id} — poll a live-login session's
 * lifecycle (applies the 10-minute window server-side). A 404 surfaces as an
 * `ApiError`; the caller treats it like a terminal state (stop polling, fall
 * back to manual).
 */
export async function getConsoleConnectSession(
  sessionId: string,
): Promise<LiveConnectSessionOut> {
  const { data } = await api.get<LiveConnectSessionOut>(
    `/api/v1/console-connect/sessions/${encodeURIComponent(sessionId)}`,
  );
  return data;
}

/**
 * Partial update of mutable R1b columns. The handler emits a
 * ``mode_transitioned`` outbox event atomically when ``system_mode`` or
 * ``operator_mode_override`` change (spec L121).
 */
export async function patchApp(
  appId: string,
  body: AppPatchRequest,
  idempotencyKey: string,
): Promise<AppPatchResponse> {
  const { data } = await api.patch<AppPatchResponse>(
    `/api/v1/apps/${encodeURIComponent(appId)}`,
    body,
    { headers: { "Idempotency-Key": idempotencyKey } },
  );
  return data;
}

/**
 * Create an App row from the Onboarding Step 1 URL paste.
 *
 * Backend parses platform + store_id from the URL deterministically;
 * listing-baseline metadata is filled in asynchronously by the
 * store-pipeline worker (STORE-PIPELINE umbrella).
 */
export async function createApp(
  body: AppCreateRequest,
  idempotencyKey: string,
): Promise<AppCreateResponse> {
  const { data } = await api.post<AppCreateResponse>("/api/v1/apps", body, {
    headers: { "Idempotency-Key": idempotencyKey },
  });
  return data;
}

/**
 * Search both app stores by name for the onboarding Screen-1 pick dropdown
 * (ENG-ONBOARDING-4SCREEN-SEARCH-CONNECT Track B; experience-and-ui.md
 * § 9c Search-and-pick). Candidates arrive interleaved in each store's own
 * relevance order; `degraded_platforms` names a store whose search failed
 * (honest partial — never an empty lie).
 */
export async function searchStoreApps(q: string): Promise<StoreSearchResponse> {
  const { data } = await api.get<StoreSearchResponse>(
    "/api/v1/apps/store-search",
    { params: { q } },
  );
  return data;
}

/**
 * Queue an operator-triggered competitor or metrics scrape.
 *
 * Source: docs/product/experience-and-ui.md § 6a Manual refresh
 * (ENG-FE-SIGNALS-MANUAL-REFRESH — sub-ticket #2 of the
 * ENG-FE-DIRECTION-2026-05-09 umbrella).
 *
 * 24h rate-limit per (app_id, job_class). On rate-limit hit the backend
 * returns HTTP 422 with the canonical `rate_limited` error envelope at
 * `error.response.data.detail` — the calling hook surfaces the
 * spec-canonical disabled-state copy from that detail.
 */
export async function triggerManualRefresh(
  appId: string,
  jobClass: ManualRefreshJobClass,
  idempotencyKey: string,
): Promise<ManualRefreshResponse> {
  const { data } = await api.post<ManualRefreshResponse>(
    `/api/v1/apps/${encodeURIComponent(appId)}/refresh`,
    null,
    {
      params: { job_class: jobClass },
      headers: { "Idempotency-Key": idempotencyKey },
    },
  );
  return data;
}

/**
 * Imported-experiment-history batch trigger
 * (`POST /api/v1/apps/{app_id}/experiments/import` — HTTP_API.md;
 * ENG-EXPERIMENTS-LAUNCH-READINESS item 3, audit #19). Queues one
 * `experiment_import_requested` outbox event per batch item; the
 * `experiment_import` worker maps each pre-normalized console result
 * into a finalized Source-1 experiment with
 * `hypothesis_source="imported"`. 202 with the queued count;
 * Idempotency-Key REQUIRED (same-key same-body re-POST replays).
 */
export async function importExperiments(
  appId: string,
  body: ExperimentImportRequest,
  idempotencyKey: string,
): Promise<ExperimentImportResponse> {
  const { data } = await api.post<ExperimentImportResponse>(
    `/api/v1/apps/${encodeURIComponent(appId)}/experiments/import`,
    body,
    { headers: { "Idempotency-Key": idempotencyKey } },
  );
  return data;
}

/**
 * Full-replacement brief write (ENG-M2-CLOSE Sub-3).
 *
 * PUT /api/v1/apps/{app_id}/brief replaces the brief atomically + runs
 * analyze_brief inline + emits brief_context_updated. Distinct from
 * PATCH /api/v1/apps/{app_id} which treats `onboarding_brief` as
 * leave-alone semantics and does NOT emit the cache event when the
 * brief changes (structural gap — caches stay warm until the operator
 * explicitly calls POST /brief/reanalyze).
 *
 * The handler bumps `App.last_brief_reanalyzed_at = now` so the
 * operator cannot waste a reanalyze call on a brief PUT just analyzed.
 */
export async function putBrief(
  appId: string,
  body: OnboardingBriefWriteRequest,
  idempotencyKey: string,
): Promise<OnboardingBriefWriteResponse> {
  const { data } = await api.put<OnboardingBriefWriteResponse>(
    `/api/v1/apps/${encodeURIComponent(appId)}/brief`,
    body,
    { headers: { "Idempotency-Key": idempotencyKey } },
  );
  return data;
}

/**
 * Hard-delete an app row (ENG-M2-CLOSE Sub-2).
 *
 * DELETE /api/v1/apps/{app_id} cascades 12 child tables via Postgres
 * ON DELETE CASCADE; 3 RESTRICT child tables (asset + experiment +
 * experiment_schedule) force the operator to archive those entities
 * first. RESTRICT-then-409 surfaces as HTTP 409 with the canonical
 * `app_has_active_children` error envelope + the failed-constraint
 * name — callers MUST surface the constraint name so the operator
 * knows what to archive.
 *
 * Idempotency: callers MUST supply Idempotency-Key — deleting an
 * already-deleted app returns 404 (not 200) on the second call;
 * the idempotency key short-circuits the duplicate-emit path on the
 * outbox event.
 */
export async function deleteApp(
  appId: string,
  idempotencyKey: string,
): Promise<AppDeleteResponse> {
  const { data } = await api.delete<AppDeleteResponse>(
    `/api/v1/apps/${encodeURIComponent(appId)}`,
    { headers: { "Idempotency-Key": idempotencyKey } },
  );
  return data;
}

// The `reanalyzeBrief` service fn was retired 2026-07-29 with the FE
// [Re-analyze brief] affordance chain (ASOLOOP-UX item-6 loose-end close) —
// POST /api/v1/apps/{app_id}/brief/reanalyze (HTTP_API.md) stays a live
// API/ops surface with no SPA caller.

/**
 * POST /api/v1/apps/{app_id}/pair — explicitly link the app to its
 * opposite-platform counterpart (operator-linked pairing, ENG-APP-PAIRING).
 * Writes `apps.paired_app_id` symmetrically on both rows. No Idempotency-Key
 * (the symmetric write is naturally idempotent on re-pair).
 */
export async function pairApp(
  appId: string,
  counterpartAppId: string,
): Promise<AppPairResponse> {
  const { data } = await api.post<AppPairResponse>(
    `/api/v1/apps/${encodeURIComponent(appId)}/pair`,
    { counterpart_app_id: counterpartAppId },
  );
  return data;
}

/**
 * DELETE /api/v1/apps/{app_id}/pair — clear the operator-linked pair on both
 * sides. Idempotent: unpairing an already-unpaired app is a 200 no-op.
 */
export async function unpairApp(appId: string): Promise<AppPairResponse> {
  const { data } = await api.delete<AppPairResponse>(
    `/api/v1/apps/${encodeURIComponent(appId)}/pair`,
  );
  return data;
}

/**
 * GET /api/v1/apps/{app_id}/monthly-overviews — the app's frozen monthly AI
 * overviews, newest-first (ASOLOOP-UX item 12 3/3; HTTP_API.md). Pure
 * read over the append-only `monthly_app_overviews` rows; an absent month is
 * legitimately absent (the writer skips rather than freezing a fallback).
 */
export async function getMonthlyOverviews(
  appId: string,
): Promise<MonthlyOverviewListResponse> {
  const { data } = await api.get<MonthlyOverviewListResponse>(
    `/api/v1/apps/${encodeURIComponent(appId)}/monthly-overviews`,
  );
  return data;
}
